'use client';

import { ContextualPopup, Table } from '@/ui';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import type { FC, PropsWithChildren } from 'react';
import { useMemo } from 'react';
import { useDataPopup } from '../hooks/useDataPopup';
import { useTruth } from '../hooks/useTruth';
import { messages } from '../messages';

interface ConditionReferrerProps extends PropsWithChildren {
  facebook?: string;
  twitter?: string;
  google?: string;
  hackerNews?: string;
}

export const ConditionReferrer: FC<ConditionReferrerProps> = ({
  facebook,
  twitter,
  google,
  hackerNews,
  children,
}) => {
  const { dataBlocks } = useTruth();
  const { referrer } = dataBlocks;
  const { DataPopup } = useDataPopup();

  const [localStorage] = useLocalStorage('privacyArticle', {});

  const referrers = useMemo(() => {
    return [
      ...new Set(
        (localStorage.referrers || []).filter((referrer) => !!referrer.trim())
      ),
    ];
  }, [localStorage?.referrers]);

  const handle = useMemo(() => {
    const hasDomain = (domains: string[], matchString: string) => {
      return domains.some((url) => matchString.match(url));
    };

    if (!referrers[0]) return children;

    if (hasDomain(['t.co', 'twitter.com', 'twitter.co'], referrers[0])) {
      return twitter;
    } else if (hasDomain(['facebook.com'], referrers[0])) {
      return facebook;
    } else if (hasDomain(['google.com'], referrers[0])) {
      return google;
    } else if (hasDomain(['news.ycombinator.com'], referrers[0])) {
      return hackerNews;
    }

    return children;
  }, [referrers, children, facebook, twitter, google, hackerNews]);

  return (
    <DataPopup
      handle={handle}
      subheading={messages['condition.referrer.description']}
      dataVisualisation={
        referrers.length !== 0 && (
          <ContextualPopup.Section>
            <Table.List
              header={messages['condition.referrer.pastKnownReferrers']}
              items={referrers}
            />
          </ContextualPopup.Section>
        )
      }
      condition={{
        title: messages['condition.referrer'],
        options: [
          { title: 'Facebook', value: facebook },
          { title: 'Twitter', value: twitter },
          { title: 'Google', value: google },
          { title: 'Hacker News', value: hackerNews },
          {
            title: messages['condition.referrer.other'],
            value: children?.toString() || '',
          },
        ],
      }}
      dataBlocks={{ referrer }}
    />
  );
};
